import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

export default function Applications() {
  const [apps,    setApps]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [stFilter, setStFilter] = useState('');

  const [modal,   setModal]   = useState(null);  // 'add' | 'delete'
  const [selApp,  setSelApp]  = useState(null);
  const [name,    setName]    = useState('');
  const [saving,  setSaving]  = useState(false);
  const [nameErr, setNameErr] = useState('');

  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/applications');
      setApps(r.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openAdd    = () => { setName(''); setNameErr(''); setModal('add'); };
  const openDelete = (a) => { setSelApp(a); setModal('delete'); };
  const closeModal = () => { setModal(null); setSelApp(null); setName(''); setNameErr(''); };

  const save = async () => {
    if (!name.trim()) return setNameErr('Application name is required.');
    setSaving(true);
    try {
      await api.post('/api/applications', { name: name.trim() });
      await loadAll();
      showToast('success', 'Application added.');
      closeModal();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/applications/${selApp.id}`);
      await loadAll();
      showToast('success', 'Application deleted.');
      closeModal();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setSaving(false); }
  };

  const toggleStatus = async (a) => {
    const next = a.status === 'active' ? 'inactive' : 'active';
    try {
      await api.put(`/api/applications/${a.id}`, { status: next });
      setApps(prev => prev.map(x => x.id === a.id ? { ...x, status: next } : x));
    } catch { showToast('error', 'Status update failed.'); }
  };

  const filtered = apps.filter(a => !stFilter || a.status === stFilter);

  return (
    <Layout title="Applications">

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: '20px', right: '24px', zIndex: 4000,
          background: toast.type === 'success' ? C.successBg : C.dangerBg,
          color:      toast.type === 'success' ? C.successText : C.dangerText,
          border:     `1px solid ${toast.type === 'success' ? '#6EE7B7' : '#FCA5A5'}`,
          borderRadius: '10px', padding: '12px 20px', fontSize: '13px', fontWeight: 600,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {toast.type === 'success' ? '✓' : '⚠'} {toast.text}
        </div>
      )}

      {/* Summary + Add button */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '22px', flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'Total',    val: apps.length,                                        dot: C.primary },
          { label: 'Active',   val: apps.filter(a => a.status === 'active').length,     dot: C.success },
          { label: 'Inactive', val: apps.filter(a => a.status === 'inactive').length,   dot: C.textSub },
        ].map(({ label, val, dot }) => (
          <div key={label} style={{
            background: C.white, border: `1px solid ${C.border}`, borderRadius: '10px',
            padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px',
          }}>
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot }} />
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: C.textMain }}>{val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 500 }}>{label}</div>
            </div>
          </div>
        ))}
        <button onClick={openAdd} style={{ ...btnPrimary, marginLeft: 'auto' }}>
          + Add Application
        </button>
      </div>

      {/* Table card */}
      <div style={card}>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px', alignItems: 'center' }}>
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selStyle}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {stFilter && <button onClick={() => setStFilter('')} style={btnGhost}>Clear</button>}
          <span style={{ marginLeft: 'auto', fontSize: '12px', color: C.textSub }}>
            {filtered.length} / {apps.length}
          </span>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Application', 'ID', 'Status', 'Added On', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={5} style={emptyCell}>
                  {apps.length === 0 ? 'No applications yet. Click "+ Add Application" to start.' : 'No results.'}
                </td></tr>
              ) : filtered.map((a, i) => (
                <tr key={a.id} style={{ background: i % 2 === 0 ? C.white : '#FFFBF5', borderBottom: `1px solid ${C.border}` }}>
                  <td style={td}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '38px', height: '38px', borderRadius: '10px', flexShrink: 0,
                        background: C.primaryLight,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '18px', fontWeight: 700, color: C.primaryDeep,
                      }}>
                        {(a.name || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontWeight: 600, fontSize: '14px', color: C.textMain }}>{a.name}</span>
                    </div>
                  </td>
                  <td style={td}>
                    <span style={{
                      background: C.primaryLight, color: C.primaryDeep,
                      padding: '4px 12px', borderRadius: '6px',
                      fontSize: '12px', fontWeight: 700, fontFamily: 'monospace',
                    }}>{a.app_id}</span>
                  </td>
                  <td style={td}>
                    <button onClick={() => toggleStatus(a)} style={{
                      border: 'none', cursor: 'pointer', borderRadius: '20px',
                      padding: '5px 14px', fontSize: '12px', fontWeight: 600,
                      background: a.status === 'active' ? C.successBg : C.dangerBg,
                      color:      a.status === 'active' ? C.successText : C.dangerText,
                    }}>
                      {a.status === 'active' ? '● Active' : '○ Inactive'}
                    </button>
                  </td>
                  <td style={td}>
                    <span style={{ fontSize: '12px', color: C.textSub }}>{a.created_at || '—'}</span>
                  </td>
                  <td style={td}>
                    <SmBtn label="Delete" color={C.danger} onClick={() => openDelete(a)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Modal */}
      {modal === 'add' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '420px' }}>
            <MHeader title="Add Application" onClose={closeModal} />
            <div style={{ padding: '28px' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: C.textMain, marginBottom: '8px' }}>
                Application Name *
              </label>
              <input
                autoFocus
                value={name}
                onChange={e => { setName(e.target.value); setNameErr(''); }}
                onKeyDown={e => e.key === 'Enter' && save()}
                placeholder="e.g. Car, Van, Fleet Bus 01…"
                style={{
                  width: '100%', padding: '11px 14px', boxSizing: 'border-box',
                  border: `1.5px solid ${nameErr ? C.danger : C.primary}`,
                  borderRadius: '8px', fontSize: '14px', color: C.textMain, outline: 'none',
                }}
              />
              {nameErr && (
                <div style={{ fontSize: '12px', color: C.danger, marginTop: '6px' }}>⚠ {nameErr}</div>
              )}
            </div>
            <MFooter onClose={closeModal} onSave={save} saving={saving} label="+ Add Application" />
          </div>
        </Overlay>
      )}

      {/* Delete Modal */}
      {modal === 'delete' && (
        <Overlay>
          <div style={{ ...modalBox, maxWidth: '380px' }}>
            <MHeader title="Delete Application" onClose={closeModal} />
            <div style={{ padding: '20px 28px' }}>
              <p style={{ fontSize: '13px', color: C.textMain, lineHeight: 1.6, margin: 0 }}>
                Delete <strong style={{ color: C.danger }}>{selApp?.name}</strong>
                {' '}<span style={{ fontSize: '11px', color: C.textSub }}>({selApp?.app_id})</span>?
                <br /><span style={{ fontSize: '12px', color: C.textSub }}>This cannot be undone.</span>
              </p>
            </div>
            <MFooter onClose={closeModal} onSave={confirmDelete} saving={saving} label="Delete" danger />
          </div>
        </Overlay>
      )}
    </Layout>
  );
}

function SmBtn({ label, color, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 600,
      background: `${color}18`, color, border: `1px solid ${color}30`, cursor: 'pointer',
    }}>{label}</button>
  );
}

function Overlay({ children }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000, padding: '20px',
    }}>{children}</div>
  );
}

function MHeader({ title, onClose }) {
  return (
    <div style={{
      padding: '22px 28px', borderBottom: `1px solid ${C.border}`,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ width: '3px', height: '18px', background: C.primary, borderRadius: '2px' }} />
        <span style={{ fontSize: '15px', fontWeight: 700, color: C.textMain }}>{title}</span>
      </div>
      <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub }}>✕</button>
    </div>
  );
}

function MFooter({ onClose, onSave, saving, label, danger = false }) {
  return (
    <div style={{
      padding: '16px 28px', borderTop: `1px solid ${C.border}`,
      display: 'flex', justifyContent: 'flex-end', gap: '10px',
    }}>
      <button onClick={onClose} style={btnGhost}>Cancel</button>
      <button onClick={onSave} disabled={saving} style={danger ? btnDanger : btnPrimary}>
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

const card     = { background: C.white, borderRadius: '12px', padding: '22px', border: `1px solid ${C.border}` };
const modalBox = { background: C.white, borderRadius: '16px', width: '100%', boxShadow: '0 24px 80px rgba(0,0,0,0.3)', border: `1px solid ${C.border}` };
const th       = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td       = { padding: '14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selStyle = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary = {
  padding: '10px 20px', background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
  color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
  boxShadow: `0 3px 10px rgba(245,166,35,0.3)`,
};
const btnGhost  = { padding: '10px 18px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnDanger = { padding: '10px 18px', background: C.danger, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
